import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  buildSamplingFormAnswerDraft,
  buildFormAnswersPayload,
  defaultEnvironmentalConditionDraft,
  fieldParameterDraftFromList,
  strapiGetBeritaAcaraSamplingForm,
  type EnvironmentalConditionDraft,
  type FieldParameterDraftState,
  type FormAnswerDraftState,
} from '../../../api/formSampling';
import {
  strapiFinishSurvey,
  strapiGetSamplingPlan,
  type StrapiFinishSurveyPayload,
  type StrapiSurveyGridRow,
} from '../../../api/strapiCrm';
import { useAuth } from '../../../auth/AuthContext';
import {
  isAmbientAirQualityJenisName,
  jenisPengukuranDocumentIdFromSamplingPlan,
  jenisPengukuranNameFromSamplingPlan,
} from '../../../sampling/samplingPlanBidang';
import { readCurrentPosition, resolveSurveyStartCoordinates } from '../surveyGeo';
import { SurveyBeritaAcaraFormTabs } from './SurveyBeritaAcaraFormTabs';
import {
  emptyFinishDraft,
  finishDraftFromLetterDetail,
  finishVehicleInformationPayload,
  finishMeasurementPayload,
  finishPhotoOrKeteranganError,
  isFinishDraftLocationComplete,
  parseSamplingAirGroup,
  type FinishDraft,
} from './surveyBeritaAcaraFinishTypes';

type Props = {
  finishRow: StrapiSurveyGridRow | null;
  onClose: () => void;
  onSuccess: (args: { endTime: string | null; rowKey: string; completed: boolean }) => void;
};

export function SurveyBeritaAcaraFinishDialog({ finishRow, onClose, onSuccess }: Props) {
  const { jwt } = useAuth();
  const qc = useQueryClient();
  const [finishDraft, setFinishDraft] = useState<FinishDraft>(emptyFinishDraft);
  const [sketchFile, setSketchFile] = useState<File | null>(null);
  const [formAnswerDraft, setFormAnswerDraft] = useState<FormAnswerDraftState>({});
  const [fieldParameterDraft, setFieldParameterDraft] = useState<FieldParameterDraftState>({});
  const [environmentalConditionDraft, setEnvironmentalConditionDraft] = useState<
    EnvironmentalConditionDraft[]
  >([]);
  const [formAnswerFiles, setFormAnswerFiles] = useState<Record<string, File>>({});
  const sketchPreviewRef = useRef('');
  sketchPreviewRef.current = finishDraft.location_sketsa_picture;
  const [activeTab, setActiveTab] = useState(0);
  const [finishModalError, setFinishModalError] = useState<string | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const beritaAcaraDocumentId = finishRow?.berita_acara_document_id ?? '';
  const samplingPlanDocumentId = finishRow?.sampling_plan_document_id ?? '';

  const samplingPlanQ = useQuery({
    queryKey: ['strapi', 'samplingPlan', jwt, samplingPlanDocumentId],
    queryFn: () => strapiGetSamplingPlan(jwt!, samplingPlanDocumentId),
    enabled: Boolean(jwt && samplingPlanDocumentId && finishRow),
  });
  const jenisPengukuranDocumentId = jenisPengukuranDocumentIdFromSamplingPlan(samplingPlanQ.data);

  const samplingFormQ = useQuery({
    queryKey: ['strapi', 'beritaAcaraSamplingForm', jwt, beritaAcaraDocumentId, jenisPengukuranDocumentId],
    queryFn: () =>
      strapiGetBeritaAcaraSamplingForm(jwt!, beritaAcaraDocumentId, jenisPengukuranDocumentId),
    enabled: Boolean(jwt && beritaAcaraDocumentId && finishRow && samplingPlanQ.isSuccess),
  });

  const formGroups = samplingFormQ.data?.form_groups ?? samplingFormQ.data?.jenis_groups ?? [];
  const fieldParameters = samplingFormQ.data?.field_parameters ?? [];
  const showEnvironmentalCondition = isAmbientAirQualityJenisName(
    jenisPengukuranNameFromSamplingPlan(samplingPlanQ.data),
  );

  useEffect(() => {
    if (!finishRow) {
      setFinishDraft(emptyFinishDraft());
      setSketchFile(null);
      setFormAnswerDraft({});
      setFieldParameterDraft({});
      setEnvironmentalConditionDraft([]);
      setFormAnswerFiles({});
    setActiveTab(0);
    setFinishModalError(null);
    setGpsLoading(false);
    return;
  }
  setFinishDraft(emptyFinishDraft());
  setSketchFile(null);
  setFieldParameterDraft({});
  setEnvironmentalConditionDraft([]);
  setFormAnswerFiles({});
  setActiveTab(0);
  setFinishModalError(null);
  setGpsLoading(false);
  }, [finishRow?.row_key]);

  useEffect(() => {
    return () => {
      const url = sketchPreviewRef.current;
      if (url.startsWith('blob:')) URL.revokeObjectURL(url);
    };
  }, []);

  useEffect(() => {
    if (!samplingFormQ.data) return;
    setFormAnswerDraft(
      buildSamplingFormAnswerDraft(
        samplingFormQ.data.form_groups ?? samplingFormQ.data.jenis_groups ?? [],
        samplingFormQ.data.existing_answers,
      ),
    );
    setFieldParameterDraft(fieldParameterDraftFromList(samplingFormQ.data.field_parameters));
    const savedEnvironment = samplingFormQ.data.environmental_condition;
    const ambient = isAmbientAirQualityJenisName(jenisPengukuranNameFromSamplingPlan(samplingPlanQ.data));
    setEnvironmentalConditionDraft(
      savedEnvironment.length
        ? savedEnvironment
        : ambient
          ? defaultEnvironmentalConditionDraft()
          : [],
    );
    setFinishDraft(finishDraftFromLetterDetail(samplingFormQ.data.letter_detail));
  }, [samplingFormQ.data, samplingPlanQ.data]);

  const finishM = useMutation({
    mutationFn: async (complete: boolean) => {
      if (!jwt || !finishRow?.berita_acara_document_id) throw new Error('Berita acara tidak ditemukan.');

      const alreadyFinished = Boolean(String(finishRow.end_time ?? '').trim()) ||
        finishRow.status === 'finished' ||
        finishRow.status === 'customer_signed' ||
        finishRow.status === 'completed';
      const endTime = complete && !alreadyFinished ? new Date().toISOString() : undefined;
      const form_answers = formGroups.length
        ? buildFormAnswersPayload(formGroups, formAnswerDraft, formAnswerFiles)
        : undefined;
      const geo = resolveSurveyStartCoordinates(finishDraft.longitude, finishDraft.latitude);
      const payload: StrapiFinishSurveyPayload = {
        complete,
        end_time: endTime,
        location_name: finishDraft.location_name.trim(),
        ...finishMeasurementPayload(finishDraft),
        jumlah_sampel: finishDraft.jumlah_sampel.trim(),
        is_having_blanko_wadah_penyerap: finishDraft.is_having_blanko_wadah_penyerap,
        abnormalitas_note: finishDraft.abnormalitas_note.trim(),
        keterangan_foto: finishDraft.keterangan_foto.trim(),
        sample_point_collection: finishRow.point_label.trim(),
        ...(finishDraft.lhu_grouped_name ? { lhu_grouped_name: finishDraft.lhu_grouped_name } : {}),
        form_answers,
        jenis_pengukuran_document_id: jenisPengukuranDocumentId || undefined,
        ...(samplingFormQ.data
          ? {
              dynamic_main_parameter: {
                parameter_utama_lapangan: Object.fromEntries(
                  fieldParameters.map((param) => [
                    param.documentId,
                    (fieldParameterDraft[param.documentId] ?? '').trim(),
                  ]),
                ),
                environmental_condition: environmentalConditionDraft,
                ...(finishDraft.lhu_grouped_name === 'emisi_kendaraan'
                  ? { vehicle_information: finishVehicleInformationPayload(finishDraft) }
                  : {}),
              },
            }
          : {}),
        ...(geo ? { longitude: geo.longitude, latitude: geo.latitude } : {}),
        ...(sketchFile ? { location_sketsa_picture_file: sketchFile } : {}),
        ...(Object.keys(formAnswerFiles).length ? { form_answer_files: formAnswerFiles } : {}),
      };
      const updated = await strapiFinishSurvey(jwt, finishRow.berita_acara_document_id, payload);
      return { updated, complete, endTime: endTime ?? (updated.end_time ? String(updated.end_time) : null) };
    },
    onSuccess: async ({ complete, endTime }) => {
      await qc.invalidateQueries({
        queryKey: ['strapi', 'beritaAcaraSamplingForm', jwt, beritaAcaraDocumentId],
      });
      onSuccess({ endTime, rowKey: finishRow!.row_key, completed: complete });
    },
    onError: (e) =>
      setFinishModalError(e instanceof Error ? e.message : 'Gagal menyimpan form sampling.'),
  });

  const formLoading = samplingPlanQ.isPending || samplingFormQ.isPending;
  const formError = samplingPlanQ.error ?? samplingFormQ.error;
  const airGroup = parseSamplingAirGroup(samplingFormQ.data?.letter_detail?.air_group);
  const hasLhuGroup = Boolean(finishDraft.lhu_grouped_name);
  const canSave =
    isFinishDraftLocationComplete(finishDraft, airGroup) &&
    hasLhuGroup &&
    !formLoading &&
    !finishM.isPending &&
    !gpsLoading;
  const canComplete = !formLoading && !finishM.isPending && !gpsLoading;

  async function handleRetakeGps() {
    setGpsLoading(true);
    setFinishModalError(null);
    try {
      const geo = await readCurrentPosition();
      setFinishDraft((d) => ({
        ...d,
        longitude: String(geo.longitude),
        latitude: String(geo.latitude),
      }));
    } catch (e) {
      setFinishModalError(e instanceof Error ? e.message : 'Gagal mengambil GPS.');
    } finally {
      setGpsLoading(false);
    }
  }

  return (
    <Dialog
      open={Boolean(finishRow)}
      onClose={() => {
        if (finishM.isPending) return;
        onClose();
      }}
      fullScreen
      fullWidth
      slotProps={{
        paper: {
          sx: { display: 'flex', flexDirection: 'column' },
        },
      }}
      data-testid="survey-berita-acara-finish-dialog"
    >
      <DialogTitle>Selesai sampling — berita acara</DialogTitle>
      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        {finishModalError ? (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setFinishModalError(null)}>
            {finishModalError}
          </Alert>
        ) : null}
        {finishRow ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            {finishRow.bidang_pengujian_label} — {finishRow.point_label}
          </Typography>
        ) : null}

        {formLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : null}

        {formError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {formError instanceof Error ? formError.message : 'Gagal memuat form sampling.'}
          </Alert>
        ) : null}

        <SurveyBeritaAcaraFormTabs
          formGroups={formGroups}
          activeTab={activeTab}
          onActiveTabChange={setActiveTab}
          airGroup={airGroup}
          finishDraft={finishDraft}
          onFinishDraftChange={setFinishDraft}
          formAnswerDraft={formAnswerDraft}
          onFormAnswerDraftChange={setFormAnswerDraft}
          fieldParameters={fieldParameters}
          fieldParameterDraft={fieldParameterDraft}
          onFieldParameterDraftChange={setFieldParameterDraft}
          showEnvironmentalCondition={showEnvironmentalCondition}
          environmentalConditionDraft={environmentalConditionDraft}
          onEnvironmentalConditionDraftChange={setEnvironmentalConditionDraft}
          formAnswerFiles={formAnswerFiles}
          onFormAnswerFilesChange={setFormAnswerFiles}
          onSketchFileChange={setSketchFile}
          gpsLoading={gpsLoading}
          gpsDisabled={finishM.isPending}
          onRetakeGps={() => void handleRetakeGps()}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={finishM.isPending}>
          Batal
        </Button>
        <Button
          variant="outlined"
          data-testid="survey-berita-acara-save-submit"
          disabled={!canSave}
          onClick={() => finishM.mutate(false)}
        >
          {finishM.isPending ? 'Menyimpan…' : 'Simpan'}
        </Button>
        <Button
          variant="contained"
          data-testid="survey-berita-acara-finish-submit"
          disabled={!canComplete}
          onClick={() => {
            if (!hasLhuGroup) {
              setFinishModalError('Grup LHU wajib dipilih.');
              setActiveTab(0);
              return;
            }
            const photoError = finishPhotoOrKeteranganError(finishDraft);
            if (photoError) {
              setFinishModalError(photoError);
              setActiveTab(0);
              return;
            }
            finishM.mutate(true);
          }}
        >
          {finishM.isPending ? 'Menyimpan…' : 'Simpan dan selesai'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
